// «Домашние весы и InBody»: замеры InBody и точки Zepp на одном графике; расхождение и
// переключатель «Zepp с поправкой» (значения в базе не меняются).

import { useState } from 'react'

import { TimeSeriesChart, type ChartSeries } from '@/components/charts/TimeSeriesChart'
import { Card, ErrorText, Select } from '@/components/ui'
import { todayIso, type Period } from '@/lib/periods'

import { useCalibration, useInBodySeries, type Calibration, type ScanField } from '../api'
import { offsetFor, shiftPoints } from './logic'
import { useZeppSamples } from './zepp'
import { ZeppToggle } from './ZeppToggle'

const OPTIONS: { value: Calibration['metric']; label: string; field: ScanField; unit: string }[] = [
  { value: 'weight', label: 'Вес, кг', field: 'weight_kg', unit: 'кг' },
  { value: 'body_fat_pct', label: 'Жир, %', field: 'body_fat_pct', unit: '%' },
]

export function ZeppChartCard() {
  const [metric, setMetric] = useState<Calibration['metric']>('weight')
  const [period, setPeriod] = useState<Period>('year')
  const [adjusted, setAdjusted] = useState(false)
  const option = OPTIONS.find((o) => o.value === metric) ?? OPTIONS[0]!
  const calibration = useCalibration()
  const scans = useInBodySeries(option.field, {})
  const zepp = useZeppSamples(metric)
  const offset = offsetFor(calibration.data, metric)
  const note = Array.isArray(calibration.data)
    ? calibration.data.find((c) => c.metric === metric)?.message_ru
    : undefined
  const shifted = adjusted && offset !== null

  const zeppPoints = (zepp.data ?? [])
    .map((s) => ({ x: s.day, y: s.value as number | null }))
    .sort((a, b) => a.x.localeCompare(b.x))
  const series: ChartSeries[] = [
    {
      key: 'inbody',
      label: 'InBody',
      unit: option.unit,
      digits: 1,
      axis: 'left',
      points: (scans.data ?? []).map((p) => ({ x: p.day, y: p.value })),
    },
    {
      key: 'zepp',
      label: shifted ? 'Zepp с поправкой' : 'Zepp',
      unit: option.unit,
      digits: 1,
      axis: 'left',
      points: shiftPoints(zeppPoints, shifted ? offset : null),
    },
  ]

  return (
    <Card title="Домашние весы и InBody" subtitle={note}>
      <div className="mb-3 flex flex-wrap items-end gap-x-4 gap-y-2">
        <Select
          label="Показатель"
          value={metric}
          onChange={(event) => {
            setMetric(event.target.value as Calibration['metric'])
            setAdjusted(false)
          }}
          options={OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          containerClassName="w-48 max-w-full"
        />
        {offset !== null ? <ZeppToggle checked={adjusted} onChange={setAdjusted} /> : null}
      </div>
      {scans.error ? <ErrorText error={scans.error} /> : null}
      <TimeSeriesChart
        series={series}
        period={period}
        onPeriodChange={setPeriod}
        today={todayIso()}
        emptyText="Нет данных за этот период"
      />
    </Card>
  )
}
